(() => {
  const root = document.documentElement;
  const dialogSupported =
    typeof HTMLDialogElement !== 'undefined' &&
    typeof HTMLDialogElement.prototype.showModal === 'function';
  const productLightboxStates = new WeakMap();
  const restoreTargets = new WeakMap();
  let pointerVariantControl;

  root.classList.remove('no-js');
  root.classList.add('js');

  if (dialogSupported) root.classList.add('dialog-supported');

  const findSubmenu = (toggle) => document.getElementById(toggle.getAttribute('aria-controls'));

  const closeSubmenus = (scope, exception) => {
    scope.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
      if (toggle === exception) return;

      const submenu = findSubmenu(toggle);

      toggle.setAttribute('aria-expanded', 'false');
      if (submenu) submenu.hidden = true;
    });
  };

  const getVariantDataSource = (control) => {
    if (control instanceof HTMLSelectElement) return control.selectedOptions[0];

    return control;
  };

  const getVariantControlFromPointerTarget = (target) => {
    if (!(target instanceof Element)) return;

    const directControl = target.closest('[data-variant-input]');

    if (directControl instanceof HTMLInputElement || directControl instanceof HTMLSelectElement) {
      return directControl;
    }

    const label = target.closest('label');
    const labelledControl = label instanceof HTMLLabelElement ? label.control : undefined;

    if (
      (labelledControl instanceof HTMLInputElement ||
        labelledControl instanceof HTMLSelectElement) &&
      labelledControl.matches('[data-variant-input]')
    ) {
      return labelledControl;
    }
  };

  const parsePixelValue = (value) => {
    const number = Number.parseFloat(value);

    return Number.isFinite(number) ? number : 0;
  };

  const getProductMediaScrollBehavior = (rail, allowMotion) => {
    if (!allowMotion) return 'auto';
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return 'auto';
    }

    const railRect = rail.getBoundingClientRect();
    const isVisible = railRect.bottom > 0 && railRect.top < window.innerHeight;

    return isVisible ? 'smooth' : 'auto';
  };

  const getProductMediaItems = (rail) =>
    Array.from(rail.querySelectorAll('[data-product-media-id]')).filter(
      (item) => item instanceof HTMLElement,
    );

  const setSelectedProductMediaItem = (rail, mediaItem) => {
    getProductMediaItems(rail).forEach((item) => {
      if (item === mediaItem) {
        item.setAttribute('data-product-media-selected', 'true');
      } else {
        item.removeAttribute('data-product-media-selected');
      }
    });
  };

  const getClosestProductMediaIndex = (rail) => {
    const railRect = rail.getBoundingClientRect();

    return getProductMediaItems(rail).reduce(
      (closest, item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - railRect.left);

        return distance < closest.distance ? { distance, index } : closest;
      },
      { distance: Number.POSITIVE_INFINITY, index: 0 },
    ).index;
  };

  const updateProductMediaControls = (productSection, activeIndex) => {
    const rail = productSection.querySelector('.main-product__media-list');
    if (!(rail instanceof HTMLElement)) return;

    const mediaItems = getProductMediaItems(rail);
    const safeIndex = Math.min(Math.max(activeIndex, 0), Math.max(mediaItems.length - 1, 0));
    const previousButton = productSection.querySelector('[data-product-media-previous]');
    const nextButton = productSection.querySelector('[data-product-media-next]');
    const current = productSection.querySelector('[data-product-media-current]');

    if (previousButton instanceof HTMLButtonElement) previousButton.disabled = safeIndex === 0;
    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = safeIndex >= mediaItems.length - 1;
    }
    if (current) current.textContent = String(safeIndex + 1);
  };

  const scrollToProductMediaItem = (rail, mediaItem, allowMotion) => {
    const railStyles = window.getComputedStyle(rail);
    const mediaStyles = window.getComputedStyle(mediaItem);
    const scrollPaddingStart = parsePixelValue(
      railStyles.scrollPaddingInlineStart || railStyles.scrollPaddingLeft,
    );
    const scrollMarginStart = parsePixelValue(
      mediaStyles.scrollMarginInlineStart || mediaStyles.scrollMarginLeft,
    );
    const railRect = rail.getBoundingClientRect();
    const mediaRect = mediaItem.getBoundingClientRect();
    const maxScrollLeft = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    const nextScrollLeft = Math.min(
      Math.max(
        rail.scrollLeft +
          mediaRect.left -
          railRect.left -
          rail.clientLeft -
          scrollPaddingStart -
          scrollMarginStart,
        0,
      ),
      maxScrollLeft,
    );

    if (Math.abs(nextScrollLeft - rail.scrollLeft) <= 1) return;

    if (typeof rail.scrollTo === 'function') {
      rail.scrollTo({
        left: nextScrollLeft,
        top: rail.scrollTop,
        behavior: getProductMediaScrollBehavior(rail, allowMotion),
      });
    } else {
      rail.scrollLeft = nextScrollLeft;
    }
  };

  const normalizeProductMediaText = (value) => {
    let decodedValue = String(value || '');

    try {
      decodedValue = decodeURIComponent(decodedValue);
    } catch {
      // Keep the original text when a media URL contains an incomplete escape sequence.
    }

    return decodedValue
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, ' ')
      .trim();
  };

  const getProductMediaMatchPhrases = (variantLabel) => {
    const normalizedLabel = normalizeProductMediaText(variantLabel);
    const colorAliases = {
      burgundy: ['burgundy', 'wine red', '酒红', '酒红款'],
      red: ['red', '红色', '红色款'],
      green: ['green', '绿色', '绿色款'],
      blue: ['blue', '蓝色', '蓝色款'],
      pink: ['pink', '粉色', '粉色款'],
      cream: ['cream', 'ivory', 'off white', '米白', '米白色', '奶油色', '米白款'],
      gray: ['gray', 'grey', 'stone gray', '灰色', '灰色款'],
      grey: ['gray', 'grey', 'stone gray', '灰色', '灰色款'],
    };

    return [normalizedLabel, ...(colorAliases[normalizedLabel] || [])]
      .map(normalizeProductMediaText)
      .filter(Boolean);
  };

  const findProductMediaItem = (rail, selectedMediaId, variantLabel) => {
    const mediaItems = Array.from(rail.querySelectorAll('[data-product-media-id]'));
    const exactMatch = selectedMediaId
      ? mediaItems.find((item) => item.getAttribute('data-product-media-id') === selectedMediaId)
      : undefined;

    if (exactMatch instanceof HTMLElement) return exactMatch;

    const matchPhrases = getProductMediaMatchPhrases(variantLabel);
    if (matchPhrases.length === 0) return;

    return mediaItems.find((item) => {
      const mediaText = normalizeProductMediaText(
        `${item.getAttribute('data-product-media-label') || ''} ${
          item.getAttribute('data-product-media-source') || ''
        }`,
      );
      const mediaTokens = new Set(mediaText.split(' ').filter(Boolean));

      return matchPhrases.some((phrase) => {
        const phraseTokens = phrase.split(' ').filter(Boolean);

        return phraseTokens.length > 0 && phraseTokens.every((token) => mediaTokens.has(token));
      });
    });
  };

  const synchronizeProductMedia = (
    productSection,
    selectedMediaId,
    allowMotion = false,
    variantLabel = '',
  ) => {
    const rail = productSection.querySelector('.main-product__media-list');
    if (!(rail instanceof HTMLElement)) return;

    const mediaItem = findProductMediaItem(rail, selectedMediaId, variantLabel);
    if (!(mediaItem instanceof HTMLElement)) return;

    setSelectedProductMediaItem(rail, mediaItem);
    updateProductMediaControls(productSection, getProductMediaItems(rail).indexOf(mediaItem));
    scrollToProductMediaItem(rail, mediaItem, allowMotion);
  };

  const initializeProductMedia = (scope) => {
    const productSections = [];

    if (scope instanceof Element && scope.matches('[data-main-product]')) {
      productSections.push(scope);
    }

    scope.querySelectorAll('[data-main-product]').forEach((productSection) => {
      productSections.push(productSection);
    });

    productSections.forEach((productSection) => {
      const selectedControl = productSection.querySelector(
        'input[data-variant-input]:checked, select[data-variant-input]',
      );
      if (!(
        selectedControl instanceof HTMLInputElement || selectedControl instanceof HTMLSelectElement
      )) {
        return;
      }

      const variantData = getVariantDataSource(selectedControl);
      if (!(variantData instanceof HTMLElement)) return;

      const mediaRail = productSection.querySelector('.main-product__media-list');
      if (mediaRail instanceof HTMLElement && !mediaRail.dataset.productMediaInitialized) {
        mediaRail.dataset.productMediaInitialized = 'true';
        let mediaScrollFrame;

        mediaRail.addEventListener(
          'scroll',
          () => {
            window.cancelAnimationFrame(mediaScrollFrame);
            mediaScrollFrame = window.requestAnimationFrame(() => {
              const activeIndex = getClosestProductMediaIndex(mediaRail);
              const activeItem = getProductMediaItems(mediaRail)[activeIndex];

              if (activeItem instanceof HTMLElement) {
                setSelectedProductMediaItem(mediaRail, activeItem);
                updateProductMediaControls(productSection, activeIndex);
              }
            });
          },
          { passive: true },
        );
      }

      synchronizeProductMedia(
        productSection,
        variantData.dataset.variantMediaId,
        false,
        variantData.dataset.variantLabel,
      );
    });
  };

  const updateProductVariant = (control, allowMediaMotion = false) => {
    const productSection = control.closest('[data-main-product]');
    const variantData = getVariantDataSource(control);
    if (!productSection || !(variantData instanceof HTMLElement)) return;

    const isAvailable = variantData.dataset.variantAvailable === 'true';
    const isOnSale = variantData.dataset.variantOnSale === 'true';
    const price = productSection.querySelector('[data-product-price]');
    const currentPrice = price?.querySelector('[data-current-price]');
    const currentPriceLabel = price?.querySelector('[data-current-price-label]');
    const compareAtPrice = price?.querySelector('[data-compare-at-price]');
    const compareAtPriceLabel = price?.querySelector('[data-compare-at-price-label]');

    if (price && currentPrice && currentPriceLabel && compareAtPrice && compareAtPriceLabel) {
      currentPrice.textContent = variantData.dataset.variantPrice || '';
      currentPriceLabel.textContent = isOnSale
        ? productSection.dataset.salePriceLabel || ''
        : productSection.dataset.regularPriceLabel || '';
      compareAtPrice.textContent = isOnSale ? variantData.dataset.variantCompareAtPrice || '' : '';
      compareAtPrice.hidden = !isOnSale;
      compareAtPriceLabel.hidden = !isOnSale;
      price.classList.toggle('main-product__price--sale', isOnSale);
    }

    const addToCartButton = productSection.querySelector('[data-add-to-cart]');
    const addToCartLabel = addToCartButton?.querySelector('.button__label');

    if (addToCartButton instanceof HTMLButtonElement && addToCartLabel) {
      addToCartButton.disabled = !isAvailable;
      addToCartLabel.textContent = isAvailable
        ? productSection.dataset.addToCartLabel || ''
        : productSection.dataset.soldOutLabel || productSection.dataset.unavailableLabel || '';
    }

    synchronizeProductMedia(
      productSection,
      variantData.dataset.variantMediaId,
      allowMediaMotion,
      variantData.dataset.variantLabel,
    );

    if (control.value && typeof window.history.replaceState === 'function') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', control.value);
      window.history.replaceState(window.history.state, '', url);
    }
  };

  const updateHandwritingPersonalization = (control) => {
    const fieldset = control.closest('[data-handwriting-fieldset]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const yesChoice = fieldset.querySelector('[data-handwriting-requires-message]');
    const messagePanel = fieldset.querySelector('[data-handwriting-message-panel]');
    const messageInput = fieldset.querySelector('[data-handwriting-message-input]');

    if (
      !(yesChoice instanceof HTMLInputElement) ||
      !(messagePanel instanceof HTMLElement) ||
      !(messageInput instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    const isEnabled = yesChoice.checked;

    messagePanel.hidden = !isEnabled;
    messageInput.disabled = !isEnabled;
    messageInput.required = isEnabled;
  };

  const initializeHandwritingPersonalization = (scope) => {
    scope.querySelectorAll('[data-handwriting-fieldset]').forEach((fieldset) => {
      const selectedChoice = fieldset.querySelector('[data-handwriting-choice]:checked');

      if (selectedChoice instanceof HTMLInputElement) {
        updateHandwritingPersonalization(selectedChoice);
      }
    });
  };

  const isProductLightbox = (element) =>
    dialogSupported &&
    element instanceof HTMLDialogElement &&
    element.matches('[data-product-lightbox]');

  const getProductLightboxContext = (trigger) => {
    if (!(trigger instanceof HTMLAnchorElement)) return;

    const productSection = trigger.closest('[data-main-product]');
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));

    if (!productSection || !isProductLightbox(dialog) || !productSection.contains(dialog)) return;

    const links = Array.from(
      productSection.querySelectorAll('[data-product-lightbox-link]'),
    ).filter((link) => link instanceof HTMLAnchorElement);
    const index = links.indexOf(trigger);

    if (index < 0) return;

    return { dialog, index, links, trigger };
  };

  const renderProductLightbox = (dialog) => {
    const state = productLightboxStates.get(dialog);
    if (!state) return false;

    const link = state.links[state.index];
    const image = dialog.querySelector('[data-product-lightbox-image]');
    const counter = dialog.querySelector('[data-product-lightbox-counter]');
    const previousButton = dialog.querySelector('[data-product-lightbox-previous]');
    const nextButton = dialog.querySelector('[data-product-lightbox-next]');

    if (
      !(link instanceof HTMLAnchorElement) ||
      !(image instanceof HTMLImageElement) ||
      !counter ||
      !(previousButton instanceof HTMLButtonElement) ||
      !(nextButton instanceof HTMLButtonElement)
    ) {
      return false;
    }

    const imageWidth = Number.parseInt(link.dataset.productLightboxImageWidth || '', 10);
    const imageHeight = Number.parseInt(link.dataset.productLightboxImageHeight || '', 10);
    const counterTemplate =
      dialog.dataset.productLightboxCounterTemplate || '__CURRENT__ / __TOTAL__';

    image.src = link.href;
    image.alt = link.dataset.productLightboxAlt || '';
    if (imageWidth > 0) image.width = imageWidth;
    if (imageHeight > 0) image.height = imageHeight;

    counter.textContent = counterTemplate
      .replace('__CURRENT__', String(state.index + 1))
      .replace('__TOTAL__', String(state.links.length));
    previousButton.disabled = state.index === 0;
    nextButton.disabled = state.index === state.links.length - 1;

    return true;
  };

  const openProductLightbox = (trigger) => {
    const context = getProductLightboxContext(trigger);
    if (!context) return false;

    productLightboxStates.set(context.dialog, context);
    if (!renderProductLightbox(context.dialog)) {
      productLightboxStates.delete(context.dialog);
      return false;
    }

    try {
      if (!context.dialog.open) context.dialog.showModal();
    } catch {
      productLightboxStates.delete(context.dialog);
      return false;
    }

    context.dialog.querySelector('[data-product-lightbox-close]')?.focus();
    return true;
  };

  const moveProductLightbox = (dialog, direction) => {
    const state = productLightboxStates.get(dialog);
    if (!state) return;

    const nextIndex = Math.min(Math.max(state.index + direction, 0), state.links.length - 1);
    if (nextIndex === state.index) return;

    state.index = nextIndex;
    renderProductLightbox(dialog);
  };

  initializeProductMedia(document);
  initializeHandwritingPersonalization(document);

  document.addEventListener('focusin', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-collection-sort]')) {
      return;
    }

    event.target
      .closest('.main-collection__toolbar')
      ?.querySelector('[data-collection-filters][open]')
      ?.removeAttribute('open');
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      pointerVariantControl = getVariantControlFromPointerTarget(event.target);
    },
    true,
  );

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) {
      return;
    }

    if (event.target.matches('[data-variant-input]')) {
      const allowMediaMotion = event.isTrusted && pointerVariantControl === event.target;

      pointerVariantControl = undefined;
      updateProductVariant(event.target, allowMediaMotion);
    }
    if (event.target.matches('[data-handwriting-choice]')) {
      updateHandwritingPersonalization(event.target);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      initializeProductMedia(event.target);
      initializeHandwritingPersonalization(event.target);
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const target = event.target;

    const productMediaButton = target.closest(
      '[data-product-media-previous], [data-product-media-next]',
    );

    if (productMediaButton instanceof HTMLButtonElement) {
      const productSection = productMediaButton.closest('[data-main-product]');
      const rail = productSection?.querySelector('.main-product__media-list');

      if (productSection && rail instanceof HTMLElement) {
        const mediaItems = getProductMediaItems(rail);
        const direction = productMediaButton.matches('[data-product-media-next]') ? 1 : -1;
        const activeIndex = getClosestProductMediaIndex(rail);
        const nextIndex = Math.min(Math.max(activeIndex + direction, 0), mediaItems.length - 1);
        const nextItem = mediaItems[nextIndex];

        if (nextItem instanceof HTMLElement) {
          setSelectedProductMediaItem(rail, nextItem);
          updateProductMediaControls(productSection, nextIndex);
          scrollToProductMediaItem(rail, nextItem, true);
        }
      }
      return;
    }

    document.querySelectorAll('[data-collection-filters][open]').forEach((filters) => {
      if (!filters.contains(target)) filters.removeAttribute('open');
    });

    const submenuToggle = target.closest('[data-submenu-toggle]');

    if (submenuToggle) {
      const navigation = submenuToggle.closest('.navigation-menu');
      const submenu = findSubmenu(submenuToggle);
      if (!navigation || !submenu) return;

      const shouldOpen = submenuToggle.getAttribute('aria-expanded') !== 'true';

      document.querySelectorAll('.navigation-menu').forEach((otherNavigation) => {
        closeSubmenus(otherNavigation, otherNavigation === navigation ? submenuToggle : undefined);
      });
      submenuToggle.setAttribute('aria-expanded', String(shouldOpen));
      submenu.hidden = !shouldOpen;
      return;
    }

    const productLightboxLink = target.closest('[data-product-lightbox-link]');

    if (
      dialogSupported &&
      productLightboxLink instanceof HTMLAnchorElement &&
      openProductLightbox(productLightboxLink)
    ) {
      event.preventDefault();
      return;
    }

    const productLightbox = target.closest('[data-product-lightbox]');

    if (isProductLightbox(productLightbox)) {
      if (target.closest('[data-product-lightbox-close]')) {
        productLightbox.close();
        return;
      }

      if (target.closest('[data-product-lightbox-previous]')) {
        moveProductLightbox(productLightbox, -1);
        return;
      }

      if (target.closest('[data-product-lightbox-next]')) {
        moveProductLightbox(productLightbox, 1);
        return;
      }

      if (target === productLightbox) {
        productLightbox.close();
        return;
      }
    }

    const dialogTrigger = target.closest('[aria-haspopup="dialog"][aria-controls]');

    if (dialogSupported && dialogTrigger) {
      const dialog = document.getElementById(dialogTrigger.getAttribute('aria-controls'));
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-mobile-navigation]')) {
        return;
      }

      restoreTargets.set(dialog, dialogTrigger);
      dialogTrigger.setAttribute('aria-expanded', 'true');

      if (!dialog.open) dialog.showModal();

      root.classList.add('mobile-navigation-open');
      dialog.querySelector('[data-mobile-menu-close]')?.focus();
      return;
    }

    const closeButton = target.closest('[data-mobile-menu-close]');

    if (closeButton) {
      closeButton.closest('[data-mobile-navigation]')?.close();
      return;
    }

    const dialog = target.closest('[data-mobile-navigation]');

    if (dialog && (target === dialog || target.closest('a'))) {
      dialog.close();
      return;
    }

    document.querySelectorAll('.navigation-menu').forEach((navigation) => {
      if (!navigation.contains(target)) closeSubmenus(navigation);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element)) return;

    pointerVariantControl = undefined;

    const productLightbox = event.target.closest('[data-product-lightbox]');

    if (
      isProductLightbox(productLightbox) &&
      (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
    ) {
      event.preventDefault();
      moveProductLightbox(productLightbox, event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }

    if (event.key !== 'Escape') return;

    const navigation = event.target.closest('.navigation-menu');
    const openToggle = navigation?.querySelector('[data-submenu-toggle][aria-expanded="true"]');
    if (!navigation || !openToggle) return;

    event.preventDefault();
    closeSubmenus(navigation);
    openToggle.focus();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!dialogSupported || !(event.target instanceof HTMLDialogElement)) return;

      const dialog = event.target;

      if (dialog.matches('[data-product-lightbox]')) {
        const state = productLightboxStates.get(dialog);
        productLightboxStates.delete(dialog);

        if (state?.trigger.isConnected) state.trigger.focus();
        return;
      }

      if (!dialog.matches('[data-mobile-navigation]')) return;

      const restoreTarget = restoreTargets.get(dialog);
      const currentTrigger = document.querySelector(`[aria-controls="${dialog.id}"]`);

      currentTrigger?.setAttribute('aria-expanded', 'false');
      closeSubmenus(dialog);
      restoreTargets.delete(dialog);

      if (!document.querySelector('[data-mobile-navigation][open]')) {
        root.classList.remove('mobile-navigation-open');
      }

      if (restoreTarget?.isConnected) restoreTarget.focus();
    },
    true,
  );

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element) {
      event.target.querySelectorAll('[data-product-lightbox]').forEach((dialog) => {
        if (!isProductLightbox(dialog)) return;

        productLightboxStates.delete(dialog);
        if (dialog.open) dialog.close();
      });
    }

    requestAnimationFrame(() => {
      if (!document.querySelector('[data-mobile-navigation][open]')) {
        root.classList.remove('mobile-navigation-open');
      }
    });
  });
})();
